import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import moment from 'moment';
import { Checkbox, IconButton, FormControlLabel } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { FaStopCircle } from 'react-icons/fa';
import { MdBarChart, MdSkipNext } from 'react-icons/md';

import API from 'utils/api';
import { SectionTitle, Table, Tooltip } from 'components';
import { parseActions } from 'components/utils';
import { useSources } from 'hooks';
import { textSort } from 'components/Table';
import { getTemplateFriendlyType } from 'templates';
import { UrlBox } from '..';

import AnalyticsDialog from './AnalyticsDialog';
import StopInteractionDialog from './StopInteractionDialog';
import StopScreenDialog from './StopScreenDialog';

const InteractionList = forwardRef((props, ref) => {
  const [showClosed, setShowClosed] = useState();
  const [dialog, setDialog] = useState({});

  const instance = useRef({});
  const table = useRef({});
  const sources = useSources();
  const theme = useTheme();

  instance.current.reload = table.current.reload;
  instance.current.showClosed = showClosed;
  instance.current.eventId = props.event._id;

  useImperativeHandle(ref, () => instance.current);

  useEffect(() => {
    if (showClosed !== undefined) {
      table.current.reload();
    }
  }, [showClosed]);

  const fetchData = useCallback(
    ({ search, ...rest }) => {
      const { showClosed, eventId } = instance.current;
      const query = { event: eventId, flow: { $in: [null, undefined] } };

      if (!showClosed) {
        query.active = true;
      }

      return API.getInteractions(
        {
          ...rest,
          query: buildSearchQuery(search, query),
          select: 'name publishedAt type template active standalone params',
          interactionEngagement: true,
        },
        sources
      );
    },
    [sources]
  );

  const renderTitle = () => {
    const highlight = { fontWeight: 'bold', color: theme.palette.primary.main };

    return (
      <SectionTitle title="Interactions">
        Here are all <span style={highlight}>Interactions</span> created for the current
        event
      </SectionTitle>
    );
  };

  const renderActions = data => {
    const { _id: id, active, standalone } = data;
    const actions = parseActions(props.actions, ['stop', 'analytics', 'screen']);

    const button = (active, disabled, Icon, onClick, title) =>
      active ? (
        <IconButton disabled={disabled} color="primary" onClick={onClick}>
          <Tooltip title={title} placement="top">
            <Icon />
          </Tooltip>
        </IconButton>
      ) : null;

    return (
      <div>
        {button(
          actions.stop,
          !active,
          FaStopCircle,
          () => setDialog({ interaction: data }),
          'Click to stop this Interaction'
        )}

        {button(
          actions.analytics,
          false,
          MdBarChart,
          () => setDialog({ analytics: id }),
          'Click to view Interaction Analytics'
        )}

        {button(
          actions.screen,
          !active,
          MdSkipNext,
          () => setDialog({ screens: data }),
          'Click to close some screens'
        )}

        {active && standalone ? (
          <UrlBox
            title="Stand-Alone Interaction Links"
            path={`/interaction/${id}`}
            demo
          />
        ) : null}
      </div>
    );
  };

  const renderControlPanel = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!showClosed}
              onChange={() => setShowClosed(x => !x)}
              color="primary"
            />
          }
          label="Show closed Interactions"
        />
      </div>
    );
  };

  const onCloseDialog = () => setDialog({});

  return (
    <>
      <Table
        tableRef={table}
        pageSize={props.pageSize || 5}
        rowStyle={({ active }) => (active ? undefined : { opacity: 0.6 })}
        columns={[
          {
            title: 'Template Name',
            field: 'params.category',
            defaultSort: 'desc',
            weight: 1.5,
          },
          {
            title: 'Display Title',
            field: 'name',
            weight: 1.5,
          },
          {
            title: 'Type',
            field: 'type',
            customSort: textSort('type', getTemplateFriendlyType),
            render: ({ type }) => getTemplateFriendlyType(type),
            weight: 1.2,
          },
          {
            title: 'Published At',
            field: 'publishedAt',
            align: 'center',
            weight: 1.2,
            render: ({ publishedAt }) => (
              <div>{moment(publishedAt).format('MMMM Do YYYY, h:mm:ss a')}</div>
            ),
          },
          {
            title: (
              <div
                style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}
              >
                <span>Received</span> / <span>Opened</span>
              </div>
            ),
            align: 'center',
            sorting: false,
            render: ({ msgReceived, msgOpened }) =>
              `${msgReceived || 0}/${msgOpened || 0}`,
          },
          {
            title: 'Engagement',
            align: 'center',
            sorting: false,
            field: 'engagement',
            weight: 1.2,
            render: ({ engagement }) => `${engagement || 0}%`,
          },
          {
            title: 'Actions',
            sorting: false,
            align: 'center',
            render: renderActions,
            weight: 1.5,
          },
        ]}
        data={fetchData}
        title={renderTitle()}
        controlPanel={renderControlPanel()}
      />

      <AnalyticsDialog
        interactionId={dialog.analytics}
        onClose={onCloseDialog}
        onMessage={props.onMessage}
      />

      <StopInteractionDialog interaction={dialog.interaction} onClose={onCloseDialog} />
      <StopScreenDialog interaction={dialog.screens} onClose={onCloseDialog} />
    </>
  );
});

export default InteractionList;

const buildSearchQuery = (search, query) => {
  if (search) {
    const filter = { $regex: search, $options: 'i' };

    return JSON.stringify({
      ...query,
      $or: [{ 'params.category': filter }, { name: filter }],
    });
  }

  return query ? JSON.stringify(query) : undefined;
};
